import React from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";

export default function DataPractices() {
    return (
        <div className="min-h-screen bg-stone-50">
            <div className="max-w-3xl mx-auto px-6 py-16">
                <Link to="/" className="text-xs font-outfit uppercase tracking-[0.2em] text-stone-500 hover:text-emerald-700">
                    ← Back to Infini HealthCare
                </Link>
                <div className="mt-6 flex items-center gap-3">
                    <Lock className="w-8 h-8 text-emerald-700" />
                    <h1 className="font-outfit font-semibold text-4xl text-stone-900 tracking-tight">
                        Data Practices &amp; HIPAA Compliance
                    </h1>
                </div>

                <div className="mt-8 font-dmsans text-stone-700 leading-relaxed space-y-6">
                    <section>
                        <h2 className="font-outfit font-semibold text-xl text-stone-900 mb-3">Our HIPAA posture</h2>
                        <p>Infini HealthCare handles a subset of information that constitutes Protected Health Information (PHI) under 45 CFR § 160.103. We treat all PHI in accordance with the HIPAA Security Rule (administrative, physical, and technical safeguards) and the Privacy Rule (use and disclosure limitations).</p>
                    </section>

                    <section>
                        <h2 className="font-outfit font-semibold text-xl text-stone-900 mb-3">Business Associate Agreements</h2>
                        <p>We have executed (or are in process of executing) BAAs with the following subcontractors that may process PHI on our behalf:</p>
                        <table className="w-full text-sm mt-3 border border-stone-200 rounded-xl overflow-hidden">
                            <thead className="bg-stone-100 text-xs uppercase text-stone-500 font-outfit">
                                <tr>
                                    <th className="p-3 text-left">Vendor</th>
                                    <th className="p-3 text-left">Role</th>
                                    <th className="p-3 text-left">Status</th>
                                </tr>
                            </thead>
                            <tbody className="[&>tr]:border-t [&>tr]:border-stone-200">
                                <tr><td className="p-3">Stripe, Inc.</td><td className="p-3">Payments</td><td className="p-3 text-emerald-700 font-semibold">BAA available</td></tr>
                                <tr><td className="p-3">Resend</td><td className="p-3">Transactional email</td><td className="p-3 text-emerald-700 font-semibold">BAA available (Pro plan)</td></tr>
                                <tr><td className="p-3">MongoDB Atlas</td><td className="p-3">Database</td><td className="p-3 text-emerald-700 font-semibold">BAA available</td></tr>
                                <tr><td className="p-3">Twilio</td><td className="p-3">SMS (optional)</td><td className="p-3 text-emerald-700 font-semibold">BAA available</td></tr>
                                <tr><td className="p-3">Apollo.io</td><td className="p-3">Admin lead search</td><td className="p-3 text-stone-500">Never receives PHI</td></tr>
                            </tbody>
                        </table>
                    </section>

                    <section>
                        <h2 className="font-outfit font-semibold text-xl text-stone-900 mb-3">Safeguards in place</h2>
                        <ul className="list-disc pl-6 space-y-1">
                            <li><strong>Encryption in transit:</strong> TLS 1.2+ on every request; HSTS enforced</li>
                            <li><strong>Encryption at rest:</strong> AES-256 via MongoDB Atlas + object storage encryption</li>
                            <li><strong>Access controls:</strong> role-based (super_admin / coo / regional_manager / admin / agency / caregiver / family), least privilege, state-scoped for regional managers</li>
                            <li><strong>Audit logging:</strong> every administrative action + PHI access is recorded with actor, IP, timestamp</li>
                            <li><strong>Session management:</strong> JWT with expiring tokens, brute-force lockout, automatic session purge</li>
                            <li><strong>Backups:</strong> nightly encrypted backups with 14-day rotation</li>
                            <li><strong>Incident response:</strong> a documented breach-notification process with a 60-day maximum notification window per § 164.404</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="font-outfit font-semibold text-xl text-stone-900 mb-3">What we do NOT store</h2>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>Full raw payment card numbers (handled by Stripe as PCI-DSS Level 1 processor)</li>
                            <li>Social Security Numbers of caregivers (except last-4 for 1099 reporting via Stripe Connect)</li>
                            <li>Medical diagnoses or lab results (unless a family voluntarily uploads them to a care plan)</li>
                            <li>Clinical notes from licensed providers</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="font-outfit font-semibold text-xl text-stone-900 mb-3">Your rights</h2>
                        <p>Under HIPAA § 164.524, you have the right to access, inspect, and receive a copy of your PHI. Use <em>Settings → Privacy → Export my data</em> in your dashboard, or email <a className="text-emerald-700 underline" href="mailto:privacy@careco24.com">privacy@careco24.com</a>. We respond within 30 days.</p>
                    </section>

                    <section>
                        <h2 className="font-outfit font-semibold text-xl text-stone-900 mb-3">Report a security concern</h2>
                        <p>Please email <a className="text-emerald-700 underline" href="mailto:security@careco24.com">security@careco24.com</a> with details. We do not have a bug bounty at this time but we acknowledge every legitimate report and will not pursue legal action against good-faith researchers.</p>
                    </section>

                    <p className="text-xs text-stone-400 pt-8 border-t border-stone-200">
                        Executed BAAs and our full Security &amp; Compliance package (SOC 2 report when available) are shared with enterprise customers under NDA. Contact <a className="text-emerald-700 underline" href="mailto:compliance@careco24.com">compliance@careco24.com</a>.
                    </p>
                </div>
            </div>
        </div>
    );
}
